import { useEffect, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { HubConnectionState } from '@microsoft/signalr';

import { itemKeys, listKeys, type ListItemsSnapshot } from '../features/items/itemsApi.ts';
import { invalidateSuggestions, upsertItem } from '../features/items/useItems.ts';
import { invalidateItemImages } from '../features/items/useItemImages.ts';
import { invalidateAisles } from '../features/items/useAisles.ts';
import { groupKeys } from '../features/groups/groupsApi.ts';
import { onResume } from '../lifecycle/resume.ts';
import { flush } from '../offline/queueStore.ts';
import { createListConnection, joinList, leaveList } from './listConnection.ts';
import {
  HubEvent,
  type ActorInfo,
  type ConnectionState,
  type ItemAddedEvent,
  type ItemCheckedEvent,
  type ItemDeletedEvent,
  type ItemUncheckedEvent,
  type ItemUpdatedEvent,
  type ListClearedEvent,
  type PresenceChangedEvent,
} from './hubTypes.ts';

interface UseListRealtimeResult {
  connection: ConnectionState;
  /** Who currently has this list open, including the current user. */
  present: ActorInfo[];
}

/**
 * How long "Connecting…" is allowed to be the answer before the indicator admits defeat.
 *
 * Retrying continues underneath — this only changes the label. Someone in a shop watching
 * a spinner that has said the same thing for a minute has been told nothing; "Offline ·
 * changes are saved" is both true and the reassurance they actually need. It goes back to
 * live on its own the moment a connection lands, so there is nothing to tap.
 */
const StallCeilingMs = 20_000;

/**
 * Keeps the React Query cache in step with the list hub.
 *
 * Events are applied straight into the cache rather than triggering a refetch: a refetch
 * per event would be a round trip for every tick anyone makes, which on a shared list in
 * a shop is constant. The cache writer compares versions, so an echo of a change already
 * applied optimistically is dropped, and events arriving out of order settle correctly.
 *
 * That is the happy path. The rest of this file is the unhappy one, which is where a
 * shared list is actually judged: the socket having died silently while the phone was in
 * a pocket, and the app coming back showing an hour-old shop. Three things guard it —
 * the connection is re-established rather than abandoned, every successful join
 * reconciles against the server, and reopening the app is treated as an event in its own
 * right rather than waiting for SignalR to notice on its own schedule.
 */
export function useListRealtime(listId: string | undefined): UseListRealtimeResult {
  const queryClient = useQueryClient();
  const [connection, setConnection] = useState<ConnectionState>('connecting');
  const [present, setPresent] = useState<ActorInfo[]>([]);

  useEffect(() => {
    if (!listId) {
      return;
    }

    // A narrowed const to close over: the callbacks below outlive this check.
    const list = listId;

    const hub = createListConnection();

    let cancelled = false;
    let retryTimer: number | undefined;
    let stallTimer: number | undefined;
    let stalled = false;
    let retryingSince = 0;

    // These arrive on this list's channel and are applied straight into this list's
    // cache — which is only sound because the server never sends another list's item
    // events here. Note that none of them reads event.listId.
    hub.on(HubEvent.ItemAdded, (event: ItemAddedEvent) =>
      upsertItem(queryClient, list, event.item),
    );

    hub.on(HubEvent.ItemChecked, (event: ItemCheckedEvent) =>
      upsertItem(queryClient, list, event.item),
    );

    hub.on(HubEvent.ItemUnchecked, (event: ItemUncheckedEvent) =>
      upsertItem(queryClient, list, event.item),
    );

    hub.on(HubEvent.ItemUpdated, (event: ItemUpdatedEvent) =>
      upsertItem(queryClient, list, event.item),
    );

    hub.on(HubEvent.ItemDeleted, (event: ItemDeletedEvent) => {
      queryClient.setQueryData<ListItemsSnapshot>(itemKeys.list(list), (snapshot) =>
        snapshot
          ? { ...snapshot, items: snapshot.items.filter((item) => item.id !== event.itemId) }
          : snapshot,
      );
    });

    hub.on(HubEvent.ListCleared, (event: ListClearedEvent) => {
      const cleared = new Set(event.itemIds);

      queryClient.setQueryData<ListItemsSnapshot>(itemKeys.list(list), (snapshot) =>
        snapshot
          ? { ...snapshot, items: snapshot.items.filter((item) => !cleared.has(item.id)) }
          : snapshot,
      );
    });

    // The chips moved, and this is the *only* thing that says so.
    //
    // It arrives on the household's channel rather than this list's, which is what makes
    // it the whole rule: chips are ranked from the group's entire history, so an add, a
    // rename, a delete or a clear on any list moves them on every list. The item handlers
    // above used to each invalidate as well, which looked like belt and braces and was in
    // fact the bug — they only ever fire for changes made to the list already on screen,
    // so a name added to the weekly shop never reached anyone holding a different list.
    hub.on(HubEvent.SuggestionsChanged, () => void invalidateSuggestions(queryClient));

    // Somebody photographed something. No item changed and no version moved — a photo
    // belongs to a name, and that name may be on several of the group's lists — so the
    // honest message is "the catalogue moved" and the client goes and reads it. Also on
    // the household's channel, so the other phone in the aisle gets the eye icon whichever
    // of the group's lists it happens to have open.
    hub.on(HubEvent.ItemImagesChanged, () => void invalidateItemImages(queryClient));

    // Somebody moved a name into a different aisle, or reordered the aisles themselves.
    // The household's channel again, and this one is the clearest case for it: an aisle is
    // filed against a name, so the people who need telling are exactly the ones holding a
    // *different* list — on this one the correction has already been applied optimistically
    // by whoever made it. Sent on the list channel it would reach only them.
    hub.on(HubEvent.AislesChanged, () => void invalidateAisles(queryClient));

    hub.on(HubEvent.PresenceChanged, (event: PresenceChangedEvent) => {
      setPresent(event.present);
    });

    // A new member means a new colour to render, so the group needs refetching.
    const refreshGroups = () => void queryClient.invalidateQueries({ queryKey: groupKeys.all });
    hub.on(HubEvent.MemberJoined, refreshGroups);
    hub.on(HubEvent.MemberLeft, refreshGroups);

    hub.onreconnecting(() => {
      if (!stalled) {
        setConnection('connecting');
      }

      startStallCeiling();
    });

    hub.onreconnected(() => void goLive());

    hub.onclose(() => {
      if (cancelled) {
        return;
      }

      setConnection('offline');
      scheduleRetry();
    });

    // Reopening the app is the signal that matters most and the one SignalR cannot give
    // us: a phone thawing out of a background freeze holds a socket the server let go of
    // some time ago, and nothing notices until the next keepalive is missed — up to
    // ClientTimeoutInterval later, spent showing somebody else's shop.
    const stopListeningForResume = onResume(() => void wake());

    void connect();

    return () => {
      cancelled = true;

      window.clearTimeout(retryTimer);
      window.clearTimeout(stallTimer);
      stopListeningForResume();

      void (async () => {
        if (hub.state === HubConnectionState.Connected) {
          await leaveList(hub, list);
        }
        await hub.stop();
      })();
    };

    /**
     * Brings the connection up, from wherever it currently is.
     *
     * A connection already on its way back is left alone — SignalR's own retry loop is
     * driving it and racing that would produce two.
     */
    async function connect(): Promise<void> {
      if (cancelled) {
        return;
      }

      if (hub.state === HubConnectionState.Connected) {
        await goLive();
        return;
      }

      if (hub.state !== HubConnectionState.Disconnected) {
        return;
      }

      setConnection(stalled ? 'offline' : 'connecting');
      startStallCeiling();

      try {
        await hub.start();
      } catch {
        if (!cancelled) {
          scheduleRetry();
        }
        return;
      }

      if (!cancelled) {
        await goLive();
      }
    }

    /**
     * Retries a start that failed.
     *
     * `withAutomaticReconnect` only covers a connection that has succeeded at least once,
     * so without this an initial start that failed was terminal: the app spent the whole
     * visit with no socket and no way back short of a manual reload. Which is the common
     * case on a phone, whose radio is very often not attached yet at the moment the app
     * is opened. The delays mirror the reconnect policy in listConnection.
     */
    function scheduleRetry(): void {
      if (cancelled) {
        return;
      }

      retryingSince ||= Date.now();

      window.clearTimeout(retryTimer);
      retryTimer = window.setTimeout(
        () => void connect(),
        Date.now() - retryingSince < 60_000 ? 2_000 : 10_000,
      );
    }

    /**
     * Connected: reconcile, rejoin, and say so.
     *
     * The reconcile runs on *every* successful join, not only on a reconnect. The list on
     * screen at this moment came from the service worker's cache as often as not — it is
     * network-first with a short timeout, and a phone whose radio is still waking up
     * misses that timeout easily — so "we have a socket" is precisely the moment to stop
     * trusting it. It also goes first, because it is the part that still matters if the
     * socket turns out to be dead after all.
     */
    async function goLive(): Promise<void> {
      if (cancelled) {
        return;
      }

      setConnection('syncing');

      try {
        await reconcile(queryClient, list);
      } catch {
        // Not fatal, and not worth stalling the indicator over: the poll in ListView
        // covers a reconcile that could not reach the server.
      }

      if (cancelled) {
        return;
      }

      const result = await joinList(hub, list);

      if (cancelled) {
        return;
      }

      setPresent(result?.present ?? []);
      clearStallCeiling();
      retryingSince = 0;
      setConnection('live');
    }

    /**
     * The app has just been brought back to the front.
     */
    async function wake(): Promise<void> {
      if (cancelled) {
        return;
      }

      if (hub.state === HubConnectionState.Disconnected) {
        // Nothing is going to bring it back on its own, and whatever backoff we were
        // waiting out was measured against a network that has since changed.
        window.clearTimeout(retryTimer);
        retryingSince = 0;
        await connect();
        return;
      }

      if (hub.state === HubConnectionState.Connected) {
        // The socket claims to be up. It very often is not — see the note on the
        // subscription above — and rejoining is the cheapest way to find out, because
        // JoinList is idempotent server-side and fails rather than hanging if the
        // connection is gone.
        await goLive();
        return;
      }

      // Connecting or reconnecting, so SignalR is already on it. The list on screen is
      // stale *now*, though, and that is the thing someone standing in a shop cares
      // about — so refresh the data without waiting for the socket to catch up.
      setConnection('syncing');

      try {
        await reconcile(queryClient, list);
      } catch {
        // As above.
      }

      if (!cancelled) {
        setConnection(stalled ? 'offline' : 'connecting');
      }
    }

    function startStallCeiling(): void {
      // Not restarted if one is already pending: retrying every two seconds would
      // otherwise push the ceiling out for ever and it would never be reached.
      if (stalled || stallTimer !== undefined) {
        return;
      }

      stallTimer = window.setTimeout(() => {
        stallTimer = undefined;
        stalled = true;

        if (!cancelled) {
          setConnection('offline');
        }
      }, StallCeilingMs);
    }

    function clearStallCeiling(): void {
      window.clearTimeout(stallTimer);
      stallTimer = undefined;
      stalled = false;
    }
  }, [listId, queryClient]);

  return { connection, present };
}

/**
 * Re-reads everything on screen from the server.
 *
 * Broader than the items alone, because a gap in the events is a gap in all of it: a list
 * created, a household renamed or a member joining while the app was away are equally
 * invisible, and all three change what is drawn.
 */
async function reconcile(queryClient: QueryClient, listId: string): Promise<void> {
  // Unsent taps go first. Reconciling with mutations still queued would re-read a server
  // that has not been told about them yet, briefly wiping them off the screen before the
  // flush put them back — and "my items disappeared" is a worse bug than the one this is
  // fixing. flush() joins a flush already in progress rather than skipping past it.
  await flush();

  await Promise.all([
    queryClient.invalidateQueries({ queryKey: itemKeys.list(listId) }),
    queryClient.invalidateQueries({ queryKey: listKeys.all }),
    queryClient.invalidateQueries({ queryKey: groupKeys.all }),
    invalidateSuggestions(queryClient),
    // A photo attached while the app was away is exactly as invisible as a list created
    // while it was away, and it decides whether a row shows an eye at all.
    invalidateItemImages(queryClient),
    // And an aisle corrected while it was away decides which section a row is drawn in,
    // which is a good deal more visible than an icon.
    invalidateAisles(queryClient),
  ]);
}
